import { Box, Button, Flex, Link, Text } from '@chakra-ui/react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { useAppToast } from '../../helpers'
import { acceptAttribute } from '../FileInput'
import { FormField } from '../FormField'
import { Icon } from '../Icon'

interface IVideoFileInputProps {
  /** Newly selected file (takes precedence over the existing URL for preview). */
  file: File | null
  /** URL of the video already stored (edit mode); shown until a new file is picked. */
  existingUrl?: string | null
  /** Called with the picked File, or null when the selection/existing video is removed. */
  onChange: (file: File | null) => void
  /** Called when the user removes the currently stored (existing) video. */
  onRemoveExisting?: () => void
  label?: string
  /** Obrigatoriedade: o asterisco vem do `FormField`, não do texto do rótulo. */
  isRequired?: boolean
  helperText?: string
  isDisabled?: boolean
  /** Max size in MB (default 100). */
  maxSizeMB?: number
  /** Backend validation errors for this field. */
  error?: string[]
  /**
   * Whether the stored video can be cleared (default true). False where the field is
   * mandatory — the testimonial video, for one — so the button does not offer a
   * removal the form would refuse to save. Dropping a freshly picked file is always
   * allowed: that only falls back to the stored video.
   */
  allowRemoveExisting?: boolean
}

const ACCEPT = acceptAttribute('video')

/** Páginas de player que a tag <video> não sabe tocar (precisariam de iframe). */
const EMBED_HOSTS =
  /^(?:https?:\/\/)?(?:www\.|m\.|player\.)?(?:vimeo\.com|youtube\.com|youtu\.be|youtube-nocookie\.com)\//i

/**
 * Se a URL guardada é reproduzível direto na tag <video>.
 *
 * Um valor migrado chega como URL absoluta do storage (MP4), e um registro ainda
 * não migrado chega como o link antigo do Vimeo/YouTube — ambos absolutos, então
 * não dá para separar pelo protocolo. Só o segundo grupo é que a <video> não toca.
 */
export function isDirectVideoSource(url?: string | null): boolean {
  return Boolean(url) && !EMBED_HOSTS.test(url as string)
}

/**
 * MP4-only video picker mirroring ImageCropInput's UX but without cropping: pick a
 * file, preview it inline, and remove it. Used for the event video and each
 * testimonial slot, which now upload an MP4 to MinIO instead of storing a Vimeo link.
 */
export default function VideoFileInput({
  file,
  existingUrl,
  onChange,
  onRemoveExisting,
  label,
  isRequired = false,
  helperText,
  isDisabled = false,
  maxSizeMB = 100,
  error,
  allowRemoveExisting = true
}: IVideoFileInputProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const toast = useAppToast()

  // Object URL for the freshly picked file (revoked on change/unmount to avoid leaks).
  const [objectUrl, setObjectUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!file) {
      setObjectUrl(null)
      return
    }
    const url = URL.createObjectURL(file)
    setObjectUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  // Um link legado continua sendo "vídeo guardado" (o botão precisa dizer "Trocar"
  // e a remoção segue valendo), mas não pode ir para o src da <video>: renderizava
  // um player preto e o formulário parecia estar sem vídeo nenhum.
  const existingIsPlayable = isDirectVideoSource(existingUrl)
  const previewSrc = useMemo(
    () => objectUrl ?? (existingIsPlayable ? existingUrl : null) ?? null,
    [objectUrl, existingUrl, existingIsPlayable]
  )
  const hasVideo = Boolean(objectUrl) || Boolean(existingUrl)
  const showsLegacyLink = !objectUrl && Boolean(existingUrl) && !existingIsPlayable
  const canRemove = hasVideo && (Boolean(file) || allowRemoveExisting)

  const handlePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0]
    // Allow re-picking the same file later.
    e.target.value = ''
    if (!picked) return

    const isMp4 = picked.type === 'video/mp4' || picked.name.toLowerCase().endsWith('.mp4')
    if (!isMp4) {
      toast.error('Formato inválido', 'Envie um arquivo de vídeo MP4.')
      return
    }

    if (picked.size > maxSizeMB * 1024 * 1024) {
      toast.error('Arquivo muito grande', `O vídeo não pode ter mais de ${maxSizeMB}MB.`)
      return
    }

    onChange(picked)
  }

  const handleRemove = () => {
    if (file) {
      // Drop the freshly picked file; falls back to the existing video (if any).
      onChange(null)
      return
    }
    // No new pick: removing means clearing the stored video.
    onRemoveExisting?.()
  }

  return (
    // O campo passa pelo `FormField`, como os demais: rótulo, obrigatoriedade e erro numa
    // convenção só.
    <FormField label={label} error={error} isRequired={isRequired} isDisabled={isDisabled}>
      {showsLegacyLink && (
        <Box mb={2} p={3} borderRadius="md" borderWidth="1px" borderColor="orange.200" bg="orange.50">
          <Text fontSize="sm" color="orange.800">
            O vídeo salvo ainda é um link antigo, que não pode ser pré-visualizado aqui. Envie um MP4 para substituí-lo.
          </Text>
          <Link href={existingUrl as string} isExternal fontSize="xs" color="orange.700" textDecoration="underline">
            Abrir o vídeo atual
          </Link>
        </Box>
      )}

      {previewSrc && (
        <Box mb={2} borderRadius="md" overflow="hidden" maxW="360px" bg="black">
          <video
            key={previewSrc}
            src={previewSrc}
            controls
            muted
            playsInline
            style={{ width: '100%', maxHeight: '240px', display: 'block' }}
          />
        </Box>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        onChange={handlePick}
        disabled={isDisabled}
        style={{ display: 'none' }}
      />

      <Flex gap={2} wrap="wrap">
        <Button
          leftIcon={<Icon name={hasVideo ? 'lni lni-video' : 'lni lni-upload'} />}
          onClick={() => inputRef.current?.click()}
          isDisabled={isDisabled}
          size="sm"
          variant="outline"
        >
          {hasVideo ? 'Trocar vídeo' : 'Selecionar vídeo MP4'}
        </Button>

        {canRemove && (
          <Button
            leftIcon={<Icon name="lni lni-trash-can" />}
            onClick={handleRemove}
            isDisabled={isDisabled}
            size="sm"
            variant="ghost"
            colorScheme="red"
          >
            Remover
          </Button>
        )}
      </Flex>

      {file && (
        <Text variant="caption" mt={1} noOfLines={1}>
          {file.name}
        </Text>
      )}

      {helperText && (
        <Text variant="caption" mt={1}>
          {helperText}
        </Text>
      )}
    </FormField>
  )
}
